import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LogOut, Inbox, Mail, Trash2, RefreshCw, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new: 'bg-amber-100 text-amber-800',
  contacted: 'bg-blue-100 text-blue-800',
  closed: 'bg-green-100 text-green-800'
};

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem('admin_token', data.access_token);
      onLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-900 to-green-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-green-700" size={28} />
          </div>
          <h1 className="text-2xl font-bold text-amber-950">Admin Login</h1>
          <p className="text-gray-500 text-sm mt-1">Cornerstone Exim - Inquiry Dashboard</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input
            type="email"
            data-testid="admin-email-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
          />
          <input
            type="password"
            data-testid="admin-password-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
          />
          {error && <p data-testid="admin-login-error" className="text-red-600 text-sm">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-green-700 hover:bg-green-800 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [authed, setAuthed] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/inquiries`, { headers: authHeaders() });
      setInquiries(data);
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('admin_token')) {
      fetchInquiries();
    } else {
      setAuthed(false);
    }
  }, [fetchInquiries]);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/api/admin/inquiries/${id}`, { status }, { headers: authHeaders() });
    setInquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    await axios.delete(`${API}/api/admin/inquiries/${id}`, { headers: authHeaders() });
    setInquiries((prev) => prev.filter((q) => q.id !== id));
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
  }

  if (!authed) {
    return <AdminLogin onLogin={fetchInquiries} />;
  }

  const newCount = inquiries.filter((q) => q.status === 'new').length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <header className="bg-green-800 text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center space-x-3">
          <Inbox size={24} />
          <div>
            <h1 className="text-lg font-bold">Inquiry Dashboard</h1>
            <p className="text-green-200 text-xs">Cornerstone Exim</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchInquiries}
            data-testid="admin-refresh-btn"
            className="flex items-center space-x-2 bg-green-700 hover:bg-green-600 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow p-5 text-center">
            <p className="text-3xl font-bold text-amber-950" data-testid="stat-total">{inquiries.length}</p>
            <p className="text-gray-500 text-sm">Total Inquiries</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 text-center">
            <p className="text-3xl font-bold text-amber-600" data-testid="stat-new">{newCount}</p>
            <p className="text-gray-500 text-sm">New</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 text-center">
            <p className="text-3xl font-bold text-green-700" data-testid="stat-handled">{inquiries.length - newCount}</p>
            <p className="text-gray-500 text-sm">Handled</p>
          </div>
        </div>

        <div className="space-y-4">
          {inquiries.length === 0 && (
            <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500">No inquiries yet.</div>
          )}
          {inquiries.map((q) => (
            <div key={q.id} className="bg-white rounded-xl shadow p-6" data-testid={`inquiry-card-${q.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-[250px]">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-bold text-amber-950 text-lg">{q.name}</h3>
                    <span className={`px-3 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[q.status] || STATUS_STYLES.new}`}>
                      {q.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {q.company} • {q.country} •{' '}
                    <a href={`mailto:${q.email}`} className="text-green-700 hover:underline">{q.email}</a>
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {q.created_at ? new Date(q.created_at).toLocaleString() : ''}
                  </p>
                  <p className="mt-2 text-sm font-semibold text-green-800">{q.product}</p>
                  <p className="mt-2 text-gray-700 whitespace-pre-line">{q.message}</p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <select
                    value={q.status || 'new'}
                    onChange={(e) => updateStatus(q.id, e.target.value)}
                    data-testid={`inquiry-status-select-${q.id}`}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-600 outline-none"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                  <div className="flex gap-2">
                    <a
                      href={`mailto:${q.email}?subject=Re: Your inquiry to Cornerstone Exim`}
                      className="flex items-center space-x-1 bg-green-700 hover:bg-green-800 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                    >
                      <Mail size={14} />
                      <span>Reply</span>
                    </a>
                    <button
                      onClick={() => deleteInquiry(q.id)}
                      data-testid={`inquiry-delete-btn-${q.id}`}
                      className="flex items-center space-x-1 bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
